import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// Manages a single Support Hours row.
//
//   * Toggles the `disabled` attribute on the row's time inputs from the
//     "Open" switch — times are editable only when the day is open.
//   * Auto-saves the open/closed state immediately (like the email-template
//     Active toggle) and shows a toast, so the row's Save button is reserved
//     for time/timezone edits.
//
// Wire on the row element:
//   data-controller="support-hours-row"
//   data-support-hours-row-url-value="<toggle endpoint>"
//   data-support-hours-row-day-value="monday"
//   data-support-hours-row-label-value="Monday"
// On the open switch:
//   data-action="change->support-hours-row#toggle"
//   data-support-hours-row-target="openToggle"
// On each time input:
//   data-support-hours-row-target="timeInput"
export default class extends Controller {
  static targets = ["openToggle", "timeInput"]
  static values = { url: String, day: String, label: { type: String, default: "Day" } }

  // Sync the disabled state from the current switch on connect so a Stimulus
  // reconnect (e.g. Turbo Frame re-render) doesn't leave the time inputs out
  // of sync with their row's switch.
  connect() {
    this.syncDisabled()
  }

  // Called on switch change: reflect the disabled state, then persist.
  toggle() {
    this.syncDisabled()
    this.save()
  }

  syncDisabled() {
    const open = this.openToggleTarget.checked
    this.timeInputTargets.forEach(input => { input.disabled = !open })
  }

  async save() {
    if (!this.hasUrlValue) return

    const open = this.openToggleTarget.checked
    const body = new FormData()
    body.append("day", this.dayValue)
    body.append("open", open ? "1" : "0")

    try {
      const response = await patch(this.urlValue, { body, responseKind: "json" })
      if (response.ok) {
        this.notify(
          `${this.labelValue} set to ${open ? "open" : "closed"}`,
          open ? "bg-success" : "bg-danger"
        )
      } else {
        this.revert(open)
      }
    } catch {
      this.revert(open)
    }
  }

  // Roll the switch back to its previous state and re-sync inputs on failure.
  revert(attemptedOpen) {
    this.openToggleTarget.checked = !attemptedOpen
    this.syncDisabled()
    this.notify("Failed to update support hours. Please try again.", "bg-danger")
  }

  notify(message, variant) {
    if (typeof window.showToast === "function") {
      window.showToast(message, variant)
    }
  }
}
